import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { LearnWorkbenchShell } from "../components/layout/LearnWorkbenchShell";
import { PracticeSurface } from "../components/practice/PracticeSurface";
import {
  NOTAS,
  MODOS,
  notasDelModo,
  calcularPortal,
  calcularArmadura,
  acordeTonica,
  tensionesModo,
  acordesDisparadores,
  buildModoPositions,
  gradosDelModo,
  gradosEscalaModo,
  generateModoFingerings,
} from "../lib/modoEngine";
import {
  fingeringToPlaybackSequence,
  neckPositionsToPlaybackSequence,
  type ExplorePlaybackRange,
} from "../lib/explore3nps";
import { ExploreSandboxTransport } from "../components/scales/ExploreSandboxTransport";
import { getNoteAtPosition, getNeckGeometry, type NoteName, type NeckPosition } from "../lib/guitarEngine";
import { getInstrumentConfig } from "../lib/instrumentEngine";
import { useInstrument } from "../contexts/InstrumentContext";
import { usePracticeMode } from "../contexts/PracticeModeContext";
import { useExploreSandbox } from "../hooks/useExploreSandbox";
import { useWorkbench } from "../components/layout/WorkbenchLayout";
import { usePitchDetector } from "../hooks/usePitchDetector";
import { useGuitarSynth } from "../hooks/useGuitarSynth";
import { useStudentFavorites } from "../hooks/useStudentFavorites";
import { PracticeIntroModal } from "../components/PracticeIntroModal";
import { TaskCreatorButton } from "../components/TaskCreatorButton";
import { Bookmark } from "lucide-react";
import { serializeModeState } from "../lib/taskEngine";
import {
  fingeringToNeckPositions,
  buildBoxConnectionSequence,
  type FretboardPattern,
} from "../lib/fingeringEngine";
import type { NeckConnection } from "../components/practice/PracticeSurface";
import { HelpTooltip } from "../components/ui/HelpTooltip";
import { useSpaciousWorkbench } from "../lib/useSpaciousWorkbench";
import { ModoImmersiveCanvas } from "../components/modos/ModoImmersiveCanvas";

const BRILLO_BG: Record<string, string> = {
  "-5": "#1a1a2e",
  "-4": "#16213e",
  "-3": "#2d1b4e",
  "-2": "#533483",
  "-1": "#7a5eb8",
  "0": "#B5A8F5",
  "1": "#FFF8E1",
};

const BOX_COLORS = [
  "#FF4D4D", "#2DD4BF", "#22C55E", "#A07840", "#8B5A7C", "#4A6B8A", "#C44B4B", "#2C8F5E",
];

const PATTERNS = [
  { value: "linear", label: "Lineal" },
  { value: "thirds", label: "Saltos 3ra" },
  { value: "fourths", label: "Saltos 4ta" },
  { value: "fifths", label: "Saltos 5ta" },
];

export const ModoExplorerPage: React.FC = () => {
  const [tonica, setTonica] = useState<NoteName>("C");
  const [brillo, setBrillo] = useState(0);
  const [stringOrder, setStringOrder] = useState<"tab" | "physical">("physical");
  const [leftHanded, setLeftHanded] = useState(false);
  const [focusLevel, setFocusLevel] = useState<1 | 2 | 3>(3);

  // ─── Nuevos estados (copiados de ScaleExplorer) ───
  const [octaves, setOctaves] = useState(1);
  const [direction, setDirection] = useState<"asc" | "desc" | "both">("asc");
  const [pattern, setPattern] = useState("linear");
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentSeqIndex, setCurrentSeqIndex] = useState(0);
  const [bpm, setBpm] = useState(60);
  const [sandboxPlaybackRange, setSandboxPlaybackRange] = useState<ExplorePlaybackRange>("octave");
  const [activeNote, setActiveNote] = useState<{ string: number; fret: number } | null>(null);
  const [practiceFeedback, setPracticeFeedback] = useState<"success" | "fail" | "silence" | null>(null);
  const [practiceScore, setPracticeScore] = useState(0);
  const [practiceAttempts, setPracticeAttempts] = useState(0);
  const [practiceStartTime, setPracticeStartTime] = useState<number | null>(null);
  const [practiceElapsed, setPracticeElapsed] = useState(0);
  const [practicePhase, setPracticePhase] = useState<"countdown" | "listen" | "success" | "fail" | "silence">("listen");
  const [showPracticeIntro, setShowPracticeIntro] = useState(false);
  const [selectedFingerings, setSelectedFingerings] = useState<number[]>([]);
  type RouteMode = "off" | "tonic" | "transition" | "recorrido";
  const [routeMode, setRouteMode] = useState<RouteMode>("off");

  // ─── Camino personalizado (Ctrl+Click) ───
  const [customSequence, setCustomSequence] = useState<NeckPosition[]>([]);
  const [isBuildingCustomPath, setIsBuildingCustomPath] = useState(false);

  const { instrument, surface } = useInstrument();
  const { hideInspector } = useSpaciousWorkbench();
  const { mode, setMode } = usePracticeMode();
  const isSandbox = useExploreSandbox();

  useEffect(() => {
    if (!isSandbox) return;
    setFocusLevel(3);
    if (mode === "practice") setMode("learn");
  }, [isSandbox, mode, setMode]);
  const { playFret } = useGuitarSynth();
  const { toggleFavorite, isFavorite } = useStudentFavorites();
  const { setLeftCollapsed } = useWorkbench();
  const instrumentConfig = getInstrumentConfig(instrument);
  const stringCount = instrumentConfig.strings.length;
  const frets = instrumentConfig.fretCount;

  const neckGeometry = useMemo(() => {
    return getNeckGeometry({
      frets: instrumentConfig.fretCount,
      scaleLength: instrumentConfig.scaleLength,
      targetBoardWidth: instrumentConfig.targetBoardWidth,
      paddingLeft: instrumentConfig.paddingLeft,
    });
  }, [instrumentConfig]);

  const modo = useMemo(() => MODOS.find((m) => m.brillo === brillo) || MODOS[5], [brillo]);
  const notas = useMemo(() => notasDelModo(tonica, modo.id), [tonica, modo.id]);
  const portal = useMemo(() => calcularPortal(tonica, modo.id), [tonica, modo.id]);
  const armadura = useMemo(() => calcularArmadura(tonica, modo.id), [tonica, modo.id]);
  const acorde = useMemo(() => acordeTonica(tonica, modo.id), [tonica, modo.id]);
  const tensiones = useMemo(() => tensionesModo(tonica, modo.id), [tonica, modo.id]);
  const disparadores = useMemo(() => acordesDisparadores(modo.id, tonica), [modo.id, tonica]);
  const grados = useMemo(() => gradosDelModo(modo.id), [modo.id]);
  const baseNeckPositions = useMemo(() => buildModoPositions(tonica, modo.id, 15), [tonica, modo.id]);

  const fingerings = useMemo(
    () => generateModoFingerings(tonica, modo.id, 15),
    [tonica, modo.id]
  );

  const activeSandboxFingering = useMemo(() => {
    if (!isSandbox || selectedFingerings.length === 0) return null;
    return fingerings[selectedFingerings[0]] ?? null;
  }, [isSandbox, selectedFingerings, fingerings]);

  const sandboxPlaybackSequence = useMemo(() => {
    if (!isSandbox) return null;
    if (activeSandboxFingering) {
      return fingeringToPlaybackSequence(activeSandboxFingering, tonica, sandboxPlaybackRange);
    }
    return neckPositionsToPlaybackSequence(baseNeckPositions, tonica, sandboxPlaybackRange);
  }, [
    isSandbox,
    activeSandboxFingering,
    baseNeckPositions,
    tonica,
    sandboxPlaybackRange,
  ]);

  useEffect(() => {
    if (!isSandbox) return;
    setCurrentSeqIndex(0);
    setActiveNote(null);
    setIsPlaying(false);
  }, [isSandbox, tonica, modo.id, brillo]);

  useEffect(() => {
    if (!isSandbox) return;
    setCurrentSeqIndex(0);
    setActiveNote(null);
    setIsPlaying(false);
  }, [isSandbox, selectedFingerings]);

  useEffect(() => {
    if (!isSandbox) return;
    setCurrentSeqIndex(0);
    setActiveNote(null);
    setIsPlaying(false);
  }, [isSandbox, sandboxPlaybackRange]);

  const alteraciones = armadura !== 0
    ? `${Math.abs(armadura)} ${armadura > 0 ? "sostenido(s)" : "bemol(es)"}`
    : "Sin alteraciones";

  const brilloLabel = brillo > 0 ? `+${brillo}` : String(brillo);
  const brilloColor = BRILLO_BG[String(brillo)] || BRILLO_BG["0"];

  // Refs para callbacks estables
  const modeRef = useRef(mode);
  const currentSeqIndexRef = useRef(currentSeqIndex);
  useEffect(() => { modeRef.current = mode; }, [mode]);

  useEffect(() => {
    if (mode === "practice") {
      setLeftCollapsed(true);
    }
  }, [mode, setLeftCollapsed]);
  useEffect(() => { currentSeqIndexRef.current = currentSeqIndex; }, [currentSeqIndex]);

  // ─── Secuencia de reproducción ───
  // Si hay un camino personalizado, tiene prioridad total
  const sequence = useMemo(() => {
    if (customSequence.length > 0) return customSequence;
    // Modo libre: todas las notas del modo en el mástil
    const base = baseNeckPositions.map((p) => ({ ...p }));
    if (pattern === "handFriendly") {
      base.sort((a, b) => (a.string === b.string ? a.fret - b.fret : b.string - a.string));
    } else {
      // Por defecto ordenar grave→aguda por MIDI
      base.sort((a, b) => {
        const midiA = getNoteAtPosition(a.string, a.fret).midi;
        const midiB = getNoteAtPosition(b.string, b.fret).midi;
        return midiA - midiB;
      });
    }

    const numNotes = base.length;
    if (numNotes === 0) return [];

    let indices: number[] = [];
    if (pattern === "linear" || pattern === "handFriendly") {
      for (let oct = 0; oct < octaves; oct++) {
        if (direction === "asc" || direction === "both") {
          for (let i = 0; i < numNotes; i++) indices.push(i);
        }
        if (direction === "desc" || direction === "both") {
          const start = direction === "both" ? numNotes - 2 : numNotes - 1;
          for (let i = start; i >= 0; i--) indices.push(i);
        }
      }
    } else if (pattern === "thirds") {
      for (let oct = 0; oct < octaves; oct++) {
        for (let i = 0; i < numNotes; i++) indices.push(i, (i + 2) % numNotes);
      }
    } else if (pattern === "fourths") {
      for (let oct = 0; oct < octaves; oct++) {
        for (let i = 0; i < numNotes; i++) indices.push(i, (i + 3) % numNotes);
      }
    } else if (pattern === "fifths") {
      for (let oct = 0; oct < octaves; oct++) {
        for (let i = 0; i < numNotes; i++) indices.push(i, (i + 4) % numNotes);
      }
    } else if (pattern === "boxConnection") {
      // boxConnection sin cajas seleccionadas: lineal
      for (let oct = 0; oct < octaves; oct++) {
        if (direction === "asc" || direction === "both") {
          for (let i = 0; i < numNotes; i++) indices.push(i);
        }
        if (direction === "desc" || direction === "both") {
          const start = direction === "both" ? numNotes - 2 : numNotes - 1;
          for (let i = start; i >= 0; i--) indices.push(i);
        }
      }
    }

    if (pattern !== "linear" && pattern !== "handFriendly") {
      if (direction === "desc") indices = [...indices].reverse();
      else if (direction === "both") indices = [...indices, ...[...indices].reverse().slice(1, -1)];
    }

    // Si hay cajas seleccionadas, preferimos la secuencia de cajas
    if (selectedFingerings.length > 0) {
      const selectedBoxes = selectedFingerings
        .map((idx) => fingerings[idx])
        .filter(Boolean) as FretboardPattern[];

      if (selectedBoxes.length === 0) return [];

      if (pattern === "boxConnection" || selectedBoxes.length > 1) {
        return buildBoxConnectionSequence(selectedBoxes, { direction, octaves });
      }

      const f = selectedBoxes[0];
      const notesWithMidi = f.notes.map((n) => ({
        ...n,
        midi: getNoteAtPosition(n.string, n.fret).midi,
      }));
      notesWithMidi.sort((a, b) => a.midi - b.midi);
      const boxNumNotes = notesWithMidi.length;

      let boxIndices: number[] = [];
      if (pattern === "handFriendly" || pattern === "linear") {
        for (let oct = 0; oct < octaves; oct++) {
          if (direction === "asc" || direction === "both") {
            for (let i = 0; i < boxNumNotes; i++) boxIndices.push(i);
          }
          if (direction === "desc" || direction === "both") {
            const start = direction === "both" ? boxNumNotes - 2 : boxNumNotes - 1;
            for (let i = start; i >= 0; i--) boxIndices.push(i);
          }
        }
      } else if (pattern === "thirds") {
        for (let oct = 0; oct < octaves; oct++) {
          for (let i = 0; i < boxNumNotes; i++) boxIndices.push(i, (i + 2) % boxNumNotes);
        }
      } else if (pattern === "fourths") {
        for (let oct = 0; oct < octaves; oct++) {
          for (let i = 0; i < boxNumNotes; i++) boxIndices.push(i, (i + 3) % boxNumNotes);
        }
      } else if (pattern === "fifths") {
        for (let oct = 0; oct < octaves; oct++) {
          for (let i = 0; i < boxNumNotes; i++) boxIndices.push(i, (i + 4) % boxNumNotes);
        }
      } else {
        for (let i = 0; i < boxNumNotes; i++) boxIndices.push(i);
      }

      if (pattern !== "handFriendly" && pattern !== "linear") {
        if (direction === "desc") boxIndices = [...boxIndices].reverse();
        else if (direction === "both") boxIndices = [...boxIndices, ...[...boxIndices].reverse().slice(1, -1)];
      }

      return boxIndices
        .map((idx) => notesWithMidi[idx % boxNumNotes])
        .filter(Boolean)
        .map((n) => ({
          string: n.string,
          fret: n.fret,
          note: n.note,
          degree: n.degree as any,
          highlight:
            n.degree === "R"
              ? ("root" as const)
              : n.degree === "3" || n.degree === "b3" || n.degree === "#3"
              ? ("third" as const)
              : n.degree === "5" || n.degree === "b5" || n.degree === "#5"
              ? ("fifth" as const)
              : ("scale" as const),
          finger: n.finger,
        }));
    }

    return indices
      .map((idx) => base[idx % numNotes])
      .filter(Boolean);
  }, [tonica, modo.id, octaves, direction, pattern, selectedFingerings, fingerings, baseNeckPositions, customSequence]);

  const playbackSequence = useMemo(() => {
    if (isSandbox && sandboxPlaybackSequence) return sandboxPlaybackSequence;
    return sequence;
  }, [isSandbox, sandboxPlaybackSequence, sequence]);

  // Pitch detector
  const sequenceRef = useRef(sequence);
  useEffect(() => { sequenceRef.current = sequence; }, [sequence]);
  const playbackSequenceRef = useRef(playbackSequence);
  useEffect(() => { playbackSequenceRef.current = playbackSequence; }, [playbackSequence]);

  // ─── Detección en tiempo real con YIN (modo práctica) ───
  const isPlayingRef = useRef(isPlaying);
  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);

  const handleNoteDetected = useCallback((note: NoteName, _octave: number, _frequency: number) => {
    if (!isPlayingRef.current) return;

    const idx = currentSeqIndexRef.current;
    const targetNote = sequenceRef.current[idx]?.note;
    if (!targetNote) return;

    const targetPC = NOTAS.indexOf(targetNote) % 12;
    const detectedPC = NOTAS.indexOf(note) % 12;

    setPracticeAttempts((a) => a + 1);

    if (detectedPC === targetPC) {
      setPracticeScore((s) => s + 1);
      setPracticeFeedback("success");
      setPracticePhase("success");
      setTimeout(() => { setPracticeFeedback(null); setPracticePhase("listen"); }, 700);
      const next = idx + 1;
      if (next >= sequenceRef.current.length) {
        setIsPlaying(false);
        setCurrentSeqIndex(0);
        setActiveNote(null);
      } else {
        setCurrentSeqIndex(next);
        setActiveNote({ string: sequenceRef.current[next].string, fret: sequenceRef.current[next].fret });
      }
    } else {
      setPracticeFeedback("fail");
      setPracticePhase("fail");
      setTimeout(() => { setPracticeFeedback(null); setPracticePhase("listen"); }, 700);
    }
  }, []);

  const { startListening, stopListening, state: pitchState } = usePitchDetector({
    threshold: 0.003,
    holdTimeMs: 150,
    onNoteDetected: handleNoteDetected,
  });

  const handleStartPractice = useCallback(async () => {
    setShowPracticeIntro(false);
    setCurrentSeqIndex(0);
    if (sequence.length > 0) {
      setActiveNote({ string: sequence[0].string, fret: sequence[0].fret });
    }
    setPracticeScore(0);
    setPracticeAttempts(0);
    setPracticeStartTime(Date.now());
    setPracticePhase("listen");
    await startListening();
  }, [sequence, startListening]);

  // Animación / práctica
  useEffect(() => {
    const seq = playbackSequence;
    if (!isPlaying || seq.length === 0) return;

    if (mode === "practice") {
      setShowPracticeIntro(true);
      return () => { stopListening(); };
    }

    const intervalMs = (60 / bpm) * 1000;

    if (isSandbox) {
      const useTonicAnchor = Boolean(sandboxPlaybackSequence) && seq.length > 1;
      const tonicPauseMs = Math.round(intervalMs * 1.75);
      let idx = 0;
      let timeoutId: ReturnType<typeof setTimeout>;

      const tick = () => {
        if (idx >= seq.length) {
          setIsPlaying(false);
          setCurrentSeqIndex(0);
          setActiveNote(null);
          return;
        }
        const pos = seq[idx];
        setActiveNote({ string: pos.string, fret: pos.fret });
        setCurrentSeqIndex(idx);
        playFret(pos.string, pos.fret, 600);
        idx++;
        const delay = useTonicAnchor && idx === 1 ? tonicPauseMs : intervalMs;
        timeoutId = setTimeout(tick, delay);
      };

      tick();
      return () => clearTimeout(timeoutId);
    }

    let idx = 0;
    const timer = setInterval(() => {
      if (idx >= seq.length) {
        setIsPlaying(false);
        setCurrentSeqIndex(0);
        setActiveNote(null);
        return;
      }
      const pos = seq[idx];
      setActiveNote({ string: pos.string, fret: pos.fret });
      setCurrentSeqIndex(idx);
      idx++;
    }, intervalMs);
    return () => clearInterval(timer);
  }, [
    isPlaying,
    playbackSequence,
    bpm,
    mode,
    stopListening,
    isSandbox,
    sandboxPlaybackSequence,
    playFret,
  ]);

  // Temporizador de práctica
  useEffect(() => {
    if (!practiceStartTime) { setPracticeElapsed(0); return; }
    const id = setInterval(() => setPracticeElapsed(Math.floor((Date.now() - practiceStartTime) / 1000)), 1000);
    return () => clearInterval(id);
  }, [practiceStartTime]);

  // Filtra notas de una caja CAGED para mostrar solo N octavas
  const filterCagedByOctaves = useCallback((positions: NeckPosition[], octavesCount: number): NeckPosition[] => {
    if (octavesCount <= 0 || positions.length === 0) return positions;
    const withMidi = positions.map((p) => ({
      ...p,
      midi: getNoteAtPosition(p.string, p.fret).midi,
    }));
    withMidi.sort((a, b) => a.midi - b.midi);
    const rootIndices = withMidi
      .map((n, i) => (n.degree === "R" ? i : -1))
      .filter((i) => i !== -1);
    if (rootIndices.length < 2) return positions;
    const endIdx = rootIndices[Math.min(octavesCount, rootIndices.length - 1)];
    return withMidi.slice(0, endIdx + 1);
  }, []);

  const handleNextTonica = useCallback(() => {
    const idx = NOTAS.indexOf(tonica);
    setTonica(NOTAS[idx < NOTAS.length - 1 ? idx + 1 : 0]);
    setSelectedFingerings([]);
    setIsPlaying(false);
  }, [tonica]);

  const handleSelectBrillo = useCallback((value: number) => {
    setBrillo(value);
    setSelectedFingerings([]);
    setIsPlaying(false);
  }, []);

  const modoSpotlightKeys = useMemo(() => {
    if (!isSandbox || selectedFingerings.length === 0) return undefined;
    const keys = new Set<string>();
    selectedFingerings.forEach((idx) => {
      const f = fingerings[idx];
      if (!f) return;
      f.notes.forEach((n) => keys.add(`${n.string}-${n.fret}`));
    });
    return keys;
  }, [isSandbox, selectedFingerings, fingerings]);

  const tensionesLine = useMemo(
    () => tensiones.map((t) => t.grado).join(", "),
    [tensiones]
  );

  // ─── Posiciones para el mástil ───
  const modoDegreeChips = useMemo(() => {
    const scaleDegrees = gradosEscalaModo(modo.id);
    return notas.map((note, idx) => ({
      degree: scaleDegrees[idx] ?? "?",
      note,
    }));
  }, [modo.id, notas]);

  const highlightedPositions = useMemo(() => {
    const seqForHighlight = isSandbox ? playbackSequence : sequence;

    if (isSandbox) {
      const base = baseNeckPositions.map((p) => ({ ...p }));
      if (isPlaying && seqForHighlight.length > 0) {
        return [
          ...base,
          ...seqForHighlight.map((p, i) => ({
            ...p,
            highlight: (i === currentSeqIndex ? "active" : p.highlight) as NeckPosition["highlight"],
          })),
        ];
      }
      return base;
    }

    if (selectedFingerings.length > 0) {
      const allPositions: NeckPosition[] = [];
      selectedFingerings.forEach((idx, boxIdx) => {
        const f = fingerings[idx];
        if (!f) return;
        const boxPositions = fingeringToNeckPositions(f, "scale").map((p) => ({
          ...p,
          highlight: p.degree === "R" ? ("root" as const) : ("scale" as const),
        }));
        const filtered = filterCagedByOctaves(boxPositions, octaves);
        allPositions.push(...filtered);
      });

      if (isPlaying && sequence.length > 0) {
        return [
          ...allPositions,
          ...sequence.map((p, i) => ({
            ...p,
            highlight: (i === currentSeqIndex ? "active" : p.highlight) as NeckPosition["highlight"],
          })),
        ];
      }
      return allPositions;
    }

    const base = baseNeckPositions.map((p) => ({ ...p }));
    if (isPlaying && sequence.length > 0) {
      return [
        ...base,
        ...sequence.map((p, i) => ({
          ...p,
          highlight: (i === currentSeqIndex ? "active" : "scale") as NeckPosition["highlight"],
        })),
      ];
    }
    return base;
  }, [
    isSandbox,
    isPlaying,
    sequence,
    playbackSequence,
    currentSeqIndex,
    baseNeckPositions,
    selectedFingerings,
    fingerings,
  ]);

  // ─── Focus visual: atenuar notas sobrantes ───
  const hasFocus = customSequence.length > 0;
  const dimmedKeys = useMemo(() => {
    if (!hasFocus) return undefined;
    const focusSet = new Set<string>();
    sequence.forEach((p) => focusSet.add(`${p.string}-${p.fret}`));
    return focusSet;
  }, [hasFocus, sequence]);

  // Conexiones entre cajas + camino personalizado
  const connections = useMemo(() => {
    const conns: NeckConnection[] = [];

    // Camino personalizado
    if (customSequence.length > 1) {
      for (let i = 0; i < customSequence.length - 1; i++) {
        const from = customSequence[i];
        const to = customSequence[i + 1];
        conns.push({
          from: { string: from.string, fret: from.fret },
          to: { string: to.string, fret: to.fret },
          color: "#D946EF",
          arrow: true,
          dashed: false,
          label: i === 0 ? "inicio" : undefined,
        });
      }
    }

    // 🎯 Ruta de la Tónica
    if (routeMode === "tonic") {
      const tonicPositions: { string: number; fret: number }[] = [];
      for (let s = 1; s <= stringCount; s++) {
        for (let f = 0; f <= frets; f++) {
          const n = getNoteAtPosition(s, f);
          if (n.note === tonica) {
            tonicPositions.push({ string: s, fret: f });
          }
        }
      }
      tonicPositions.sort((a, b) => {
        if (a.fret !== b.fret) return a.fret - b.fret;
        return a.string - b.string;
      });
      if (tonicPositions.length > 1 && tonicPositions.length <= 10) {
        for (let i = 0; i < tonicPositions.length - 1; i++) {
          conns.push({
            from: tonicPositions[i],
            to: tonicPositions[i + 1],
            color: "#2DD4BF",
            arrow: true,
            dashed: false,
            label: `${tonica} ${i + 1} de ${tonicPositions.length - 1}`,
          });
        }
      }
    }

    // 🐍 Ruta de Recorrido
    if (routeMode === "recorrido" && sequence.length > 1 && sequence.length <= 10) {
      for (let i = 0; i < sequence.length - 1; i++) {
        const from = sequence[i];
        const to = sequence[i + 1];
        if (from.string === to.string && from.fret === to.fret) continue;
        conns.push({
          from: { string: from.string, fret: from.fret },
          to: { string: to.string, fret: to.fret },
          color: "#D4A373",
          arrow: true,
          dashed: false,
          label: `${from.note} → ${to.note}`,
        });
      }
    }

    // 🔗 Ruta de Transición
    if (routeMode === "transition" && selectedFingerings.length >= 2) {
      const selectedBoxes = selectedFingerings
        .map((idx) => fingerings[idx])
        .filter(Boolean) as FretboardPattern[];
      const sorted = [...selectedBoxes].sort((a, b) => a.position - b.position);

      for (let i = 0; i < sorted.length - 1; i++) {
        const boxA = sorted[i];
        const boxB = sorted[i + 1];
        const notesA = boxA.notes.map((n) => ({
          ...n,
          midi: getNoteAtPosition(n.string, n.fret).midi,
        }));
        notesA.sort((a, b) => b.midi - a.midi);
        const lastNoteA = notesA[0];
        const rootNoteB = boxB.notes.find((n) => n.degree === "R");
        if (lastNoteA && rootNoteB) {
          conns.push({
            from: { string: lastNoteA.string, fret: lastNoteA.fret },
            to: { string: rootNoteB.string, fret: rootNoteB.fret },
            color: "#A07840",
            dashed: true,
            arrow: true,
            label: "→ tónica",
          });
        }
      }
    }
    return conns;
  }, [routeMode, selectedFingerings, fingerings, customSequence, sequence, tonica]);

  const toggleFingering = (idx: number) => {
    setSelectedFingerings((prev) => {
      if (prev.includes(idx)) return prev.filter((i) => i !== idx);
      return [...prev, idx];
    });
  };

  const handleFretClick = (stringNum: number, fret: number, note: NoteName) => {
    setActiveNote({ string: stringNum, fret });
    playFret(stringNum, fret);

    // Ctrl+Click o modo dibujar camino → agregar a secuencia personalizada
    if (isBuildingCustomPath) {
      setCustomSequence((prev) => {
        // Evitar duplicados consecutivos en la misma posición
        const last = prev[prev.length - 1];
        if (last && last.string === stringNum && last.fret === fret) return prev;
        return [...prev, { string: stringNum, fret, note, highlight: "scale" as const }];
      });
    }
  };

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === "Control") setIsBuildingCustomPath(true);
  }, []);

  const handleKeyUp = useCallback((e: KeyboardEvent) => {
    if (e.key === "Control") setIsBuildingCustomPath(false);
  }, []);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [handleKeyDown, handleKeyUp]);

  const [advancedOpen, setAdvancedOpen] = useState(false);

  const handleSandboxDegreeClick = useCallback(
    (degree: string) => {
      const pos =
        highlightedPositions.find((p) => p.degree === degree) ??
        baseNeckPositions.find((p) => p.note === modoDegreeChips.find((d) => d.degree === degree)?.note);
      if (pos) {
        setActiveNote({ string: pos.string, fret: pos.fret });
        playFret(pos.string, pos.fret);
      }
    },
    [highlightedPositions, baseNeckPositions, modoDegreeChips, playFret]
  );

  const handleSandboxPlay = useCallback(() => {
    if (playbackSequence.length === 0) return;
    setIsPlaying(true);
    setCurrentSeqIndex(0);
    const first = playbackSequence[0];
    setActiveNote({ string: first.string, fret: first.fret });
    playFret(first.string, first.fret);
  }, [playbackSequence, playFret]);

  const handleSandboxStop = useCallback(() => {
    setIsPlaying(false);
    setActiveNote(null);
    stopListening();
  }, [stopListening]);

  const handleSandboxNext = useCallback(() => {
    if (playbackSequence.length === 0) return;
    const next = Math.min(currentSeqIndex + 1, playbackSequence.length - 1);
    setCurrentSeqIndex(next);
    const pos = playbackSequence[next];
    setActiveNote({ string: pos.string, fret: pos.fret });
    playFret(pos.string, pos.fret);
  }, [playbackSequence, currentSeqIndex, playFret]);

  const sandboxBottom = isSandbox ? (
    <ExploreSandboxTransport
      isPlaying={isPlaying}
      onPlay={handleSandboxPlay}
      onStop={handleSandboxStop}
      onNext={handleSandboxNext}
      currentIndex={currentSeqIndex}
      sequenceLength={playbackSequence.length}
      bpm={bpm}
      onBpmChange={setBpm}
      degrees={modoDegreeChips}
      onDegreeClick={handleSandboxDegreeClick}
      playbackRange={sandboxPlaybackRange}
      onPlaybackRangeChange={setSandboxPlaybackRange}
    />
  ) : undefined;

  // ─── Sidebar de práctica ───
  const practiceSidebar = (
    <div className="space-y-4">
      {/* Header: Sesión de práctica */}
      <div className="text-center pb-3 border-b border-white/10">
        <div className="text-[10px] font-sans font-medium uppercase tracking-widest text-slate-500">Sesión de práctica</div>
        <div className="mt-2 w-16 h-16 mx-auto rounded-full bg-[var(--bordona-accent)] text-white flex items-center justify-center text-3xl font-serif font-bold shadow-lg">{tonica}</div>
        <div className="mt-2 text-lg font-serif font-bold italic text-slate-100">{modo.nombre}</div>
        <div className="text-[11px] font-sans text-slate-400">{notas.length} notas · brillo {brilloLabel}</div>
      </div>

      {/* Temporizador + puntaje */}
      <div className="p-3 rounded-xl bg-white/[0.08] border border-white/10 shadow-none">
        <div className="flex items-center justify-between">
          <div className="text-center flex-1">
            <div className="text-[10px] font-sans text-slate-400 uppercase tracking-wider">Tiempo</div>
            <div className="text-xl font-sans font-bold text-slate-100">{Math.floor(practiceElapsed / 60)}:{String(practiceElapsed % 60).padStart(2, "0")}</div>
          </div>
          <div className="w-px h-8 bg-white/15" />
          <div className="text-center flex-1">
            <div className="text-[10px] font-sans text-slate-400 uppercase tracking-wider">Precisión</div>
            <div className="text-xl font-sans font-bold text-slate-100">{practiceAttempts > 0 ? Math.round((practiceScore / practiceAttempts) * 100) : 0}%</div>
          </div>
        </div>
      </div>

      {/* Progreso */}
      <div>
        <div className="text-[10px] font-sans font-semibold uppercase tracking-wider text-slate-400 mb-2">Progreso</div>
        <div className="flex gap-1">
          {sequence.map((_, idx) => (
            <div key={idx} className={`flex-1 h-2 rounded-full transition-all ${idx < currentSeqIndex ? "bg-[#8FB069]" : idx === currentSeqIndex ? "bg-[var(--bordona-accent)]" : "bg-white/10"}`} />
          ))}
        </div>
        <div className="mt-1 text-[10px] font-sans text-slate-400">Nota {Math.min(currentSeqIndex + 1, sequence.length)} de {sequence.length}</div>
      </div>

      {/* Focus actual */}
      <div className="p-3 rounded-xl bg-white/[0.07]">
        <div className="text-[10px] font-sans font-semibold uppercase tracking-wider text-slate-400 mb-1">Focus actual</div>
        <div className="text-sm font-sans font-bold text-slate-100">{focusLevel === 1 ? "Ancla — Solo la tónica" : focusLevel === 2 ? "Triada — Tónica + 3ra + 5ta" : "Completa — Toda la escala"}</div>
      </div>

      {!isPlaying && practiceScore > 0 && (
        <div className="p-3 rounded-xl bg-white/[0.07] text-center">
          <div className="text-sm font-sans font-bold text-slate-100">¡{practiceScore}/{sequence.length} notas!</div>
          <div className="text-[10px] font-sans text-slate-400">Tiempo: {Math.floor(practiceElapsed / 60)}:{String(practiceElapsed % 60).padStart(2, "0")}</div>
          <button onClick={() => { setIsPlaying(true); setCurrentSeqIndex(0); }} className="mt-2 w-full h-8 rounded-lg bg-[var(--bordona-accent)] text-white text-xs font-sans font-semibold shadow-sm hover:bg-[var(--bordona-accent-hover)] transition-all">Volver a practicar</button>
        </div>
      )}
    </div>
  );

  // ─── Sidebar narrativo ───
  const sidebar = (
    <div className="space-y-4">
      {/* Header: Hoy exploramos */}
      <div className="text-center pb-3 border-b border-white/10">
        <div className="text-[10px] font-sans font-medium uppercase tracking-widest text-slate-500">
          Hoy exploramos
        </div>
        <div className="mt-2 w-16 h-16 mx-auto rounded-full bg-[var(--bordona-accent)] text-white flex items-center justify-center text-3xl font-serif font-bold shadow-lg">
          {tonica}
        </div>
        <div className="mt-2 text-lg font-serif font-bold italic text-slate-100">
          {modo.nombre}
        </div>
        <div className="text-[11px] font-sans text-slate-400">
          {notas.length} notas · brillo {brilloLabel}
        </div>
        <button
          onClick={() => {
            toggleFavorite({
              type: "mode",
              title: `${tonica} ${modo.nombre}`,
              subtitle: `Brillo ${brilloLabel}`,
              url: "/modos",
            });
          }}
          className={`mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-sans font-medium transition-all ${
            isFavorite("/modos")
              ? "bg-[var(--bordona-accent-soft)] text-[var(--bordona-accent)] border border-[var(--bordona-accent-border)]"
              : "bg-white/[0.07] text-slate-400 border border-white/10 hover:bg-white/10 hover:text-slate-300"
          }`}
        >
          <Bookmark className={`w-3 h-3 ${isFavorite("/modos") ? "fill-current" : ""}`} />
          {isFavorite("/modos") ? "Guardado" : "Guardar modo"}
        </button>
      </div>

      {/* Paso a paso — solo rutas guiadas (Aprender) */}
      {!isSandbox && (
      <div>
        <div className="text-[10px] font-sans font-semibold uppercase tracking-wider text-slate-400 mb-2">
          Paso {focusLevel} de 3
        </div>
        <div className="flex gap-2">
          {([
            { level: 1 as const, label: "Ancla", desc: "Encuentra la tónica", color: "#D45D79" },
            { level: 2 as const, label: "Triada", desc: "Tónica + 3ra + 5ta", color: "#2DD4BF" },
            { level: 3 as const, label: "Completa", desc: "Toda la escala", color: "#8FB069" },
          ]).map((f, idx) => {
            const isActive = focusLevel === f.level;
            const isPast = focusLevel > f.level;
            return (
              <button
                key={f.level}
                onClick={() => setFocusLevel(f.level)}
                className={`flex-1 flex flex-col items-center gap-1 py-2 rounded-xl transition-all ${
                  isActive ? "bg-white/[0.1] shadow-md ring-2 ring-[var(--bordona-accent-border)]" : isPast ? "bg-white/[0.07] opacity-80" : "bg-white/[0.07] opacity-50"
                }`}
                style={isActive ? { outline: `2px solid ${f.color}` } : {}}
                title={f.desc}
              >
                <span
                  className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${isActive || isPast ? "text-white" : "text-slate-400 bg-white/15"}`}
                  style={isActive || isPast ? { backgroundColor: f.color } : {}}
                >
                  {isPast ? "✓" : idx + 1}
                </span>
                <span className={`text-[10px] font-sans font-semibold ${isActive ? "text-slate-100" : "text-slate-400"}`}>
                  {f.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      )}

      {/* Acciones principales */}
      <div className="space-y-2">
        <button
          onClick={() => { setIsPlaying(true); setCurrentSeqIndex(0); }}
          className="w-full h-10 rounded-xl bg-[var(--bordona-accent)] text-white text-sm font-sans font-semibold flex items-center justify-center gap-2 shadow-md hover:bg-[var(--bordona-accent-hover)] transition-all"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><polygon points="5,3 19,12 5,21"/></svg>
          Escuchar este modo
        </button>
        <button
          onClick={() => setRouteMode(routeMode === "tonic" ? "off" : "tonic")}
          className={`w-full h-10 rounded-xl text-sm font-sans font-semibold flex items-center justify-center gap-2 transition-all ${
            routeMode === "tonic" ? "bg-[var(--bordona-accent)] text-white shadow-md" : "bg-white/[0.07] text-slate-300 hover:bg-white/10"
          }`}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/><path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"/><path d="M16 21h5v-5"/></svg>
          {routeMode === "tonic" ? "Ocultar ruta" : "Ver ruta de la tónica"}
        </button>
      </div>

      {/* Modo avanzado */}
      <div className="pt-2 border-t border-white/10">
        <button
          onClick={() => setAdvancedOpen(!advancedOpen)}
          className="w-full flex items-center justify-between text-[11px] font-sans font-semibold text-slate-400 hover:text-slate-100 transition-colors py-1"
        >
          <span>Modo avanzado</span>
          <svg className={`w-4 h-4 transition-transform duration-300 ${advancedOpen ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {advancedOpen && (
          <div className="mt-2 space-y-3 pb-1">
            {/* Tónica */}
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-1 block">Tónica</label>
              <div className="grid grid-cols-4 gap-1">
                {NOTAS.map((n) => (
                  <button
                    key={n}
                    onClick={() => { setTonica(n); setSelectedFingerings([]); setIsPlaying(false); }}
                    className={`h-8 rounded-md text-sm font-sans font-bold transition-all ${
                      tonica === n ? "bg-[var(--bordona-accent)] text-white shadow-sm" : "bg-white/[0.07] text-slate-300 hover:bg-white/10"
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>

            {/* Brillo */}
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-1 block">Brillo</label>
              <input
                type="range" min={-5} max={1} step={1} value={brillo}
                onChange={(e) => { setBrillo(Number(e.target.value)); setSelectedFingerings([]); setIsPlaying(false); }}
                className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                style={{ background: "linear-gradient(to right, #1a1a2e, #533483, #B5A8F5, #FFF8E1)" }}
              />
              <div className="flex justify-between text-[10px] text-slate-400 font-sans mt-1">
                <span>Locrio</span><span>Lidio</span>
              </div>
            </div>

            {/* Patrón */}
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-1 block">Patrón</label>
              <div className="grid grid-cols-2 gap-1">
                <button onClick={() => { setPattern("linear"); setDirection("asc"); }} className={`text-left px-2 py-1.5 rounded-md text-xs font-sans transition-all ${pattern === "linear" && direction === "asc" ? "bg-[var(--bordona-accent)] text-white shadow-sm" : "text-slate-300 hover:bg-white/10 bg-white/[0.07]"}`}>Lineal</button>
                <button onClick={() => { setPattern("linear"); setDirection("both"); }} className={`text-left px-2 py-1.5 rounded-md text-xs font-sans transition-all ${pattern === "linear" && direction === "both" ? "bg-[var(--bordona-accent)] text-white shadow-sm" : "text-slate-300 hover:bg-white/10 bg-white/[0.07]"}`}>Ida y vuelta</button>
                {PATTERNS.filter((p) => p.value !== "linear").map((p) => (
                  <button key={p.value} onClick={() => { setPattern(p.value); setDirection("asc"); }} className={`text-left px-2 py-1.5 rounded-md text-xs font-sans transition-all ${pattern === p.value ? "bg-[var(--bordona-accent)] text-white shadow-sm" : "text-slate-300 hover:bg-white/10 bg-white/[0.07]"}`}>{p.label}</button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ─── Inspector — Baraja de tarjetas ───
  const inspector = (
    <div className="space-y-3">
      {/* Portal de Gonzalo */}
      {portal && (
        <div
          className="p-4 rounded-2xl bg-white/[0.08] border border-white/10"
          style={{ boxShadow: "0 4px 20px rgba(45,42,38,0.04)" }}
        >
          <h4 className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-2 flex items-center gap-1.5">
            Acorde Relativo
            <HelpTooltip term="Acorde Relativo" explanation="El acorde que comparte las mismas notas que el modo actual, pero con otra tónica. Es como ver la misma escala desde otro angulo." example="En La Menor, el relativo mayor es Do Mayor. Mismas notas, distinto centro." />
          </h4>
          <div className="text-xl font-serif font-bold italic text-slate-100">{portal.nota} Mayor</div>
          <div className="text-xs text-slate-400 font-sans mt-0.5">
            Tu {portal.grado} ({portal.nombreGrado})
          </div>
          <div className="mt-3 p-3 rounded-xl bg-white/[0.06] text-slate-200 text-xs text-slate-100 font-sans italic leading-relaxed">
            “{portal.instruccion}”
          </div>
        </div>
      )}

      {/* Contexto: tónica + modo */}
      <div
        className="p-4 rounded-2xl bg-white/[0.08] border border-white/10"
        style={{ boxShadow: "0 4px 20px rgba(45,42,38,0.04)" }}
      >
        <div className="text-2xl font-serif font-bold italic text-slate-100 leading-tight">
          {tonica} {modo.nombre}
        </div>
        <div className="mt-1 text-[11px] font-sans text-slate-400">
          {notas.length} notas · brillo {brilloLabel}
        </div>
      </div>

      {/* Armadura */}
      <div
        className="p-4 rounded-2xl bg-white/[0.08] border border-white/10"
        style={{ boxShadow: "0 4px 20px rgba(45,42,38,0.04)" }}
      >
        <h4 className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-2">
          Armadura
        </h4>
        <div className="text-sm font-sans text-slate-100">{alteraciones}</div>
      </div>

      {/* Acorde de tónica */}
      {acorde && (
        <div
          className="p-4 rounded-2xl bg-white/[0.08] border border-white/10"
          style={{ boxShadow: "0 4px 20px rgba(45,42,38,0.04)" }}
        >
          <h4 className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-2">
            Acorde de tónica
          </h4>
          <div className="flex items-baseline gap-2">
            <span className="text-xl font-serif font-bold text-[#D45D79]">{acorde.siglas}</span>
            <span className="text-xs text-slate-400 font-sans">{acorde.notas.join(" — ")}</span>
          </div>
        </div>
      )}

      {/* Tensiones */}
      <div
        className="p-4 rounded-2xl bg-white/[0.08] border border-white/10"
        style={{ boxShadow: "0 4px 20px rgba(45,42,38,0.04)" }}
      >
        <h4 className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-2">
          Tensiones
        </h4>
        <div className="flex gap-3">
          {tensiones.map((t) => (
            <div key={t.grado} className="text-center">
              <div className="w-8 h-8 rounded-full bg-white/[0.07] flex items-center justify-center text-sm font-sans font-bold text-[var(--bordona-accent)]">
                {t.nota}
              </div>
              <div className="text-[10px] text-slate-400 mt-1">{t.grado}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Acordes disparadores */}
      <div
        className="p-4 rounded-2xl bg-white/[0.08] border border-white/10"
        style={{ boxShadow: "0 4px 20px rgba(45,42,38,0.04)" }}
      >
        <h4 className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-2">
          Disparan el modo
        </h4>
        <div className="space-y-2">
          {disparadores.map((ac, i) => (
            <div key={i} className="p-2.5 rounded-xl bg-white/[0.06] text-slate-200 flex items-center justify-between">
              <span className="text-xs font-sans font-semibold text-slate-100">{ac.nombre}</span>
              <span className="text-[10px] text-slate-400 font-sans italic">{ac.desc}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Cajas activas */}
      {selectedFingerings.length > 0 && (
        <div
          className="p-4 rounded-2xl bg-white/[0.08] border border-white/10"
          style={{ boxShadow: "0 4px 20px rgba(45,42,38,0.04)" }}
        >
          <h4 className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-3">
            Cajas activas
          </h4>
          <div className="space-y-2">
            {selectedFingerings.map((idx, boxIdx) => {
              const f = fingerings[idx];
              if (!f) return null;
              const color = BOX_COLORS[boxIdx % BOX_COLORS.length];
              return (
                <div key={idx} className="flex items-center gap-2.5 p-2 rounded-xl bg-white/[0.06] text-slate-200">
                  <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: color }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-sans font-semibold text-slate-100">Posición {f.position}</div>
                    <div className="text-[10px] text-slate-400 font-sans">{f.notes.length} notas · span {f.span} trastes</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <LearnWorkbenchShell
      hideLeftSidebar={isSandbox}
      immersive={isSandbox}
      bottom={sandboxBottom}
      bottomDock={isSandbox}
      sidebar={isSandbox ? undefined : mode === "practice" ? practiceSidebar : sidebar}
      inspector={isSandbox ? undefined : hideInspector ? undefined : inspector}
      title={isSandbox ? undefined : `${tonica} ${modo.nombre}`}
      activeModule="modos"
      className="min-h-[100dvh] h-[100dvh] overflow-hidden"
    >
      {isSandbox ? (
        <ModoImmersiveCanvas
          tonica={tonica}
          modoNombre={modo.nombre}
          brillo={brillo}
          notas={notas}
          portal={portal}
          acordeSiglas={acorde.siglas}
          tensionesGrados={tensionesLine}
          fingerings={fingerings}
          selectedFingerings={selectedFingerings}
          onToggleFingering={toggleFingering}
          onNextTonica={handleNextTonica}
          onSelectBrillo={handleSelectBrillo}
          highlightedPositions={highlightedPositions}
          spotlightKeys={modoSpotlightKeys}
          activeNote={activeNote}
          onFretClick={handleFretClick}
          connections={connections}
          stringOrder={stringOrder}
          leftHanded={leftHanded}
          onStringOrderToggle={() =>
            setStringOrder(stringOrder === "physical" ? "tab" : "physical")
          }
          onLeftHandedToggle={() => setLeftHanded(!leftHanded)}
          pattern={pattern}
          octaves={octaves}
          direction={direction}
        />
      ) : (
      <div className="flex-1 flex items-center justify-center p-2 min-h-0">
        <div className="w-full h-full max-w-6xl relative flex flex-col">
          {/* Barra CAGED contextual */}
          {fingerings.length > 0 && (
            <div className="shrink-0 mb-2">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[9px] font-sans font-semibold uppercase tracking-wider text-slate-500">
                  Cajas CAGED — click para seleccionar
                </span>
                {selectedFingerings.length > 0 && (
                  <button
                    onClick={() => setSelectedFingerings([])}
                    className="text-[9px] font-sans text-slate-400 hover:text-[var(--bordona-accent)] transition-colors"
                  >
                    Limpiar
                  </button>
                )}
              </div>
              <div className="relative h-8 bg-white/[0.06] rounded-lg overflow-hidden select-none">
                {[0, 3, 5, 7, 9, 12, 15].map((fret) => (
                  <div
                    key={fret}
                    className="absolute top-0 bottom-0 border-l border-white/15"
                    style={{ left: `${neckGeometry.fretPercents[fret]}%` }}
                  >
                    <span className="absolute -top-3 left-0.5 text-[8px] text-slate-400 font-sans">{fret}</span>
                  </div>
                ))}
                {fingerings
                  .map((f, i) => ({ ...f, originalIdx: i }))
                  .sort((a, b) => a.position - b.position)
                  .slice(0, 8)
                  .map((f, sortedIdx) => {
                    const isSelected = selectedFingerings.includes(f.originalIdx);
                    const color = BOX_COLORS[sortedIdx % BOX_COLORS.length];
                    const minF = Math.min(...f.notes.map((n) => n.fret));
                    const maxF = Math.max(...f.notes.map((n) => n.fret));
                    const left = neckGeometry.fretPercents[minF];
                    const rightIdx = Math.min(maxF + 1, instrumentConfig.fretCount);
                    const width = Math.max(neckGeometry.fretPercents[rightIdx] - left, 3);
                    return (
                      <button
                        key={f.originalIdx}
                        onClick={() => toggleFingering(f.originalIdx)}
                        className={`absolute top-1 bottom-1 rounded text-[9px] font-sans font-bold transition-all border ${
                          isSelected ? "shadow-sm" : "opacity-50 hover:opacity-90"
                        }`}
                        style={{
                          left: `${left}%`,
                          width: `${width}%`,
                          backgroundColor: isSelected ? color : "rgba(255,255,255,0.14)",
                          borderColor: color,
                          color: isSelected ? "#fff" : "#cbd5e1",
                        }}
                        title={`Posición ${f.position} · Trastes ${minF}-${maxF} · ${f.notes.length} notas`}
                      >
                        {f.position}
                      </button>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Área del mástil */}
          <div className="flex-1 relative min-h-0">
            <PracticeSurface
              highlightedPositions={highlightedPositions}
              activeNote={activeNote}
              onFretClick={handleFretClick}
              showNoteNames={true}
              showDegrees={false}
              interactive={true}
              stringOrder={stringOrder}
              showFingerBadges={isPlaying}
              hand="left"
              leftHanded={leftHanded}
              connections={connections}
              dimmedKeys={dimmedKeys}
              focusLevel={focusLevel}
              practiceFeedback={practiceFeedback === "silence" ? undefined : practiceFeedback ?? undefined}
              visualStyle="lutherie"
            />

            {/* Orientación flotante */}
            <div className="absolute top-2 right-2 flex flex-col gap-1.5">
              <button
                onClick={() => setStringOrder(stringOrder === "physical" ? "tab" : "physical")}
                className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-sans font-medium transition-all bg-slate-950/75 backdrop-blur-md border border-white/12 text-slate-200 hover:bg-white/10 shadow-sm"
                title={stringOrder === "physical" ? "Orden físico (grave arriba)" : "Orden tab (agudo arriba)"}
              >
                {stringOrder === "physical" ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="6" y1="3" x2="6" y2="21"/><line x1="12" y1="3" x2="12" y2="21"/><line x1="18" y1="3" x2="18" y2="21"/></svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="3" y1="14" x2="21" y2="14"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
                )}
              </button>
              <button
                onClick={() => setLeftHanded(!leftHanded)}
                className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-sans font-medium transition-all border shadow-sm ${
                  leftHanded
                    ? "bg-[var(--bordona-accent)] text-white border-[var(--bordona-accent)]"
                    : "bg-slate-950/75 backdrop-blur-md border border-white/12 text-slate-200 hover:bg-white/10"
                }`}
                title={leftHanded ? "Zurdo" : "Diestro"}
              >
                {leftHanded ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="M12 5l7 7-7 7"/></svg>
                )}
              </button>
            </div>

            {/* FAB dibujar camino */}
            <button
              onClick={() => setIsBuildingCustomPath((prev) => !prev)}
              className={`absolute bottom-4 right-4 w-11 h-11 rounded-full flex items-center justify-center shadow-lg transition-all z-10 ${
                isBuildingCustomPath || customSequence.length > 0
                  ? "bg-[#D946EF] text-white scale-110"
                  : "bg-white/[0.08] text-slate-200 hover:bg-white/12 border border-white/12"
              }`}
              title={isBuildingCustomPath ? "Terminar camino" : "Dibujar camino personalizado (Ctrl+click)"}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>
            </button>
            {isBuildingCustomPath && (
              <div className="absolute bottom-16 right-4 px-3 py-1.5 rounded-lg bg-slate-950/95 border border-white/10 text-slate-100 text-[10px] font-sans shadow-lg">
                Haz click en el mástil para agregar notas
              </div>
            )}
            {customSequence.length > 0 && !isBuildingCustomPath && (
              <button
                onClick={() => { setCustomSequence([]); setIsBuildingCustomPath(false); }}
                className="absolute bottom-4 right-16 w-9 h-9 rounded-full flex items-center justify-center bg-white/[0.08] border border-white/12 text-slate-200 hover:bg-red-500/15 hover:text-red-300 shadow-md transition-all z-10"
                title="Limpiar camino"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
              </button>
            )}

            {/* Píldora flotante de control */}
            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20">
              <div
                className="flex items-center gap-3 px-5 py-3 rounded-full bg-slate-950/88 backdrop-blur-xl border border-white/12 shadow-[0_8px_32px_rgba(0,0,0,0.35)]"
              >
                {/* ── Grupo: Ruta ── */}
                <div className="flex items-center gap-2">
                  <span className="text-[9px] font-sans font-semibold uppercase tracking-wider text-slate-500">Ruta</span>
                  <div className="flex gap-1.5">
                    {([
                      { mode: "off", label: "✕", title: "Sin ruta" },
                      { mode: "tonic", label: "🎯", title: "Ruta de la tónica" },
                      { mode: "transition", label: "🔗", title: "Transición entre cajas" },
                      { mode: "recorrido", label: "🐍", title: "Recorrido de notas" },
                    ] as const).map((r) => (
                      <button
                        key={r.mode}
                        onClick={() => setRouteMode(r.mode)}
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-xs transition-all ${
                          routeMode === r.mode
                            ? r.mode === "tonic"
                              ? "bg-[var(--bordona-accent)] text-white shadow-md scale-105"
                              : r.mode === "transition"
                              ? "bg-[#D45D79] text-white shadow-md scale-105"
                              : r.mode === "recorrido"
                              ? "bg-[#D4A373] text-white shadow-md scale-105"
                              : "bg-slate-800 text-white shadow-md"
                            : "bg-white/[0.07] text-slate-400 hover:bg-white/10"
                        }`}
                        title={r.title}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="w-px h-6 bg-white/15" />

                {/* ── Grupo: Leyenda ── */}
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-[#D45D79] ring-2 ring-[#D45D79]/20" title="Tónica" />
                    <span className="w-2.5 h-2.5 rounded-full bg-[var(--bordona-accent)] ring-2 ring-[var(--bordona-accent)]/30" title="Nota característica" />
                    <span className="w-2.5 h-2.5 rounded-full bg-[#D4A373] ring-2 ring-[#D4A373]/20" title="Escala" />
                  </div>
                </div>

                <div className="w-px h-6 bg-white/15" />

                {/* ── Grupo: Octavas ── */}
                <div className="flex items-center gap-2">
                  <span className="text-[9px] font-sans font-semibold uppercase tracking-wider text-slate-500">Oct</span>
                  <div className="flex gap-1.5">
                    {[1, 2, 3].map((o) => (
                      <button
                        key={o}
                        onClick={() => setOctaves(o)}
                        className={`w-8 h-8 rounded-full text-xs font-sans font-bold transition-all ${
                          octaves === o
                            ? "bg-[var(--bordona-accent)] text-white shadow-md"
                            : "bg-white/[0.07] text-slate-300 hover:bg-white/10"
                        }`}
                      >
                        {o}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="w-px h-6 bg-white/15" />

                {/* ── Grupo: Dirección ── */}
                <div className="flex items-center gap-2">
                  <span className="text-[9px] font-sans font-semibold uppercase tracking-wider text-slate-500">Dir</span>
                  <div className="flex gap-1.5">
                    {(["asc", "desc", "both"] as const).map((d) => (
                      <button
                        key={d}
                        onClick={() => setDirection(d)}
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                          direction === d
                            ? "bg-[var(--bordona-accent)] text-white shadow-md"
                            : "bg-white/[0.07] text-slate-300 hover:bg-white/10"
                        }`}
                        title={d === "asc" ? "Ascendente" : d === "desc" ? "Descendente" : "Ida y vuelta"}
                      >
                        {d === "asc" ? (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19V5"/><path d="M5 12l7-7 7 7"/></svg>
                        ) : d === "desc" ? (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14"/><path d="M19 12l-7 7-7-7"/></svg>
                        ) : (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M7 16V4"/><path d="M17 10v12"/><path d="M7 4l-5 5"/><path d="M17 22l5-5"/></svg>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="w-px h-6 bg-white/15" />

                {/* ── Grupo: Play + BPM ── */}
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      if (isPlaying) {
                        setIsPlaying(false);
                        setActiveNote(null);
                        if (mode === "practice") stopListening();
                      } else {
                        setIsPlaying(true);
                        setCurrentSeqIndex(0);
                      }
                    }}
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-all shadow-md ${
                      isPlaying ? "bg-slate-800 text-white" : "bg-[var(--bordona-accent)] text-white hover:bg-[var(--bordona-accent-hover)] hover:scale-105"
                    }`}
                  >
                    {isPlaying ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><polygon points="5,3 19,12 5,21"/></svg>
                    )}
                  </button>

                  <div className="flex flex-col items-center gap-0.5">
                    <span className="text-[10px] font-sans font-bold text-slate-300">{bpm}</span>
                    <input
                      type="range"
                      min={40}
                      max={200}
                      value={bpm}
                      onChange={(e) => setBpm(Number(e.target.value))}
                      className="w-20 h-1.5 accent-[var(--bordona-accent)]"
                    />
                  </div>
                </div>

                {/* Estado */}
                {isPlaying && sequence.length > 0 && (
                  <span className="text-xs font-sans font-bold text-[var(--bordona-accent)]">
                    {currentSeqIndex + 1}/{sequence.length}
                  </span>
                )}
                {mode === "practice" && isPlaying && (
                  <div className="flex items-center gap-2">
                    <span className="relative flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500" />
                    </span>
                    <span className="text-[10px] font-sans text-green-600 font-medium">
                      {pitchState.isListening ? "Escuchando…" : "Toca"}
                    </span>
                  </div>
                )}

                <div className="w-px h-6 bg-white/15" />

                {/* Brillo */}
                <span
                  className="px-2.5 py-1 rounded-full text-[10px] font-sans font-bold text-white shadow-sm"
                  style={{ backgroundColor: brilloColor }}
                >
                  {brilloLabel}
                </span>

                {/* Task Creator */}
                <TaskCreatorButton
                  type="mode"
                  variant="compact"
                  title={`Modo ${tonica} ${modo.nombre}`}
                  description={`Brillo: ${brilloLabel}, ${PATTERNS.find((p) => p.value === pattern)?.label}, ${octaves} octava(s)`}
                  config={serializeModeState(tonica, brillo, pattern, octaves, direction)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      <PracticeIntroModal
        isOpen={showPracticeIntro}
        onStart={handleStartPractice}
        onClose={() => { setShowPracticeIntro(false); setIsPlaying(false); }}
        title="Modo Práctica"
        missionText={`Toca el modo ${MODOS.find((m) => m.id === String(brillo))?.nombre || brillo} de ${tonica} nota por nota.`}
      />
    </LearnWorkbenchShell>
  );
};

export default ModoExplorerPage;
